import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import Toast from "../../helpers/Toast";
import { AssistanceCheckListTaskService } from "../../services/AssistanceCheckListTaskService";
import { AssistanceCheckListTaskDTO } from "../../models/AssistanceCheckListTask/AssistanceCheckListTaskDTO";
import ButtonComponent from "../ButtonComponent";
import { Col, Row } from "reactstrap";
import { useIntl } from "react-intl";

interface Props {
  taskId: number;
  onClose: () => void;
  open: boolean;
  observationMode?: boolean;
  onSave?: (observation: string) => Promise<void>;
}

const AssistanceCheckListTaskDetailsComponent: React.FC<Props> = ({
  taskId,
  onClose,
  open,
  observationMode = false,
  onSave,
}: Props) => {
  const [taskDetails, setTaskDetails] = useState<AssistanceCheckListTaskDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const intl = useIntl();
  const service = new AssistanceCheckListTaskService();

  const [isEditing, setIsEditing] = useState(false);
  const [obsDraft, setObsDraft] = useState("");

  const executedWithObservationState = "EXECUTADA_COM_OBSERVAÇOES";


  useEffect(() => {
    const fetchTask = async () => {
      if (!open) return;
      setIsLoading(true);
      const resp = await service.GetTaskById(taskId);
      setIsLoading(false);

      if (!resp.success || !resp.obj) {
        Toast.Show("error", resp.message);
        onClose();
        return;
      }
      setTaskDetails(resp.obj);
      setObsDraft(resp.obj.observation ?? "");
    };

    fetchTask();
  }, [open, taskId]);

  const saveObs = async () => {
    if (!taskDetails) return;

    const dto = { id: taskDetails.id, observation: obsDraft };
    const resp = await service.EditObservation(dto);

    if (resp.success) {
      Toast.Show("success", "Observação atualizada com sucesso");
      setTaskDetails({ ...taskDetails, observation: obsDraft });
      setIsEditing(false);
    } else {
      Toast.Show("error", resp.message);
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        {intl.formatMessage({ id: "Screens.AssistanceCheckList.TaskDetails" })}
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <p>{intl.formatMessage({ id: "Common.Labels.Loading" })}...</p>
        ) : taskDetails ? (
          <>
            {/* Título */}
            <Row className="mb-3">
              <Col xs={12}>
                <strong>{intl.formatMessage({ id: "Screens.AssistanceCheckList.Task.Title" })}:</strong><br />
                {taskDetails.title}
              </Col>
            </Row>

            {/* Descrição */}
            <Row className="mb-3">
              <Col xs={12}>
                <strong>{intl.formatMessage({ id: "Screens.AssistanceCheckList.Task.Description" })}:</strong><br />
                {taskDetails.description || "A tarefa não possui descrição."}
              </Col>
            </Row>

            {/* Observação */}
            {taskDetails?.status?.statusEnumValue === executedWithObservationState && (
              <Row className="mb-3">
                <Col xs={12}>
                  <div className="d-flex align-items-start justify-content-between">
                    <strong>
                      {intl.formatMessage({ id: "Screens.AssistanceCheckList.Task.Observation" })}:
                    </strong>
                    {!isEditing && (
                      <Tooltip title="Editar observação">
                        <IconButton onClick={() => setIsEditing(true)} size="small">
                          <Pencil size={16} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                  {isEditing ? (
                    <textarea
                      className="form-control mt-2"
                      value={obsDraft}
                      onChange={(e) => setObsDraft(e.target.value)}
                      rows={4}
                    />
                  ) : (
                    <div className="mt-2">
                      {taskDetails.observation || "-"}
                    </div>
                  )}
                </Col>
              </Row>
            )}
            {/* Status */}
          </>
        ) : (
          <p>{intl.formatMessage({ id: "Task.Messages.NoData" })}</p>
        )}
      </DialogContent>

      <DialogActions>
        {isEditing ? (
          <>
            <ButtonComponent
              onClick={saveObs}
              text={intl.formatMessage({ id: "Common.Buttons.Save" })}
            />
            <ButtonComponent
              onClick={() => {
                setIsEditing(false);
                setObsDraft(taskDetails?.observation ?? "");
              }}
              text={intl.formatMessage({ id: "Common.Buttons.Cancel" })}
            />
          </>
        ) : (
          <ButtonComponent
            onClick={onClose}
            text={intl.formatMessage({ id: "Task.Buttons.Close" })}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssistanceCheckListTaskDetailsComponent;
